import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';
import { revitBridge, SharedParameter } from '../api/revit/revitBridge';
import type { RevitParameter, RevitElement, RevitFamily, RevitSettings, RevitApiResponse } from '../api/revit/types';
import { registerRevitModelCacheInvalidator } from '../lib/revit/modelCacheInvalidation';
import {
  fetchFamilySnapshotResult,
  refreshFamilySnapshotResult,
  type FamilySnapshotResult,
} from '../lib/revit/familySnapshotCache';
import { resolveE2eQuickFillMixedScopeSnapshot } from '../config/e2eQuickFillMixedScopeFixture';

// Timeout for API calls (30 seconds)
const API_TIMEOUT_MS = 30000;

// Helper to add timeout to a promise
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMsg: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(errorMsg)), timeoutMs)
    )
  ]);
}

function normalizeParameterScope(parameterNames?: string[]): string[] | undefined {
  const scopedNames = Array.from(
    new Set(
      (parameterNames ?? [])
        .map((parameterName) => parameterName?.trim())
        .filter((parameterName): parameterName is string => Boolean(parameterName))
    )
  ).sort((left, right) => left.localeCompare(right));

  return scopedNames.length > 0 ? scopedNames : undefined;
}

function getResponseError(response: { error?: string }): string | undefined {
  return response.error;
}

function buildLoadCacheKey(parameterNames?: string[]): string {
  return JSON.stringify(parameterNames ?? []);
}

interface RevitDataSnapshot {
  parameters: RevitParameter[];
  sharedParameters: SharedParameter[];
  elements: RevitElement[];
  families: RevitFamily[];
  settings: RevitSettings | null;
}

function cloneSnapshot(snapshot: RevitDataSnapshot): RevitDataSnapshot {
  return typeof structuredClone === 'function'
    ? structuredClone(snapshot)
    : JSON.parse(JSON.stringify(snapshot)) as RevitDataSnapshot;
}

interface RevitDataContextType {
  // Data from Revit API
  parameters: RevitParameter[];
  sharedParameters: SharedParameter[];  // All parameters from shared parameter file
  elements: RevitElement[];
  families: RevitFamily[];
  settings: RevitSettings | null;

  // Loading state
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadRevitApiData: (specificParameterNames?: string[]) => Promise<void>;
  refreshData: (specificParameterNames?: string[]) => Promise<void>;
}

const RevitDataContext = createContext<RevitDataContextType | null>(null);

interface RevitDataProviderProps {
  children: ReactNode;
}

export const RevitDataProvider: React.FC<RevitDataProviderProps> = ({ children }) => {
  const [parameters, setParameters] = useState<RevitParameter[]>([]);
  const [sharedParameters, setSharedParameters] = useState<SharedParameter[]>([]);
  const [elements, setElements] = useState<RevitElement[]>([]);
  const [families, setFamilies] = useState<RevitFamily[]>([]);
  const [settings, setSettings] = useState<RevitSettings | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const responseCacheRef = useRef(new Map<string, RevitDataSnapshot>());
  const inFlightLoadsRef = useRef(new Map<string, Promise<RevitDataSnapshot>>());

  const applySnapshot = useCallback((snapshot: RevitDataSnapshot) => {
    const next = cloneSnapshot(snapshot);
    setParameters(next.parameters);
    setSharedParameters(next.sharedParameters);
    setElements(next.elements);
    setFamilies(next.families);
    setSettings(next.settings);
    setIsLoaded(true);
    setError(null);
  }, []);

  useEffect(() => registerRevitModelCacheInvalidator(() => {
    responseCacheRef.current.clear();
    inFlightLoadsRef.current.clear();
  }), []);

  const loadRevitApiDataInternal = useCallback(async (
    specificParameterNames?: string[],
    refresh = false
  ) => {
    const scopedParameterNames = normalizeParameterScope(specificParameterNames);
    const cacheKey = buildLoadCacheKey(scopedParameterNames);
    const cachedSnapshot = refresh
      ? undefined
      : responseCacheRef.current.get(cacheKey);

    if (cachedSnapshot) {
      applySnapshot(cachedSnapshot);
      return;
    }

    const joinedLoad = inFlightLoadsRef.current.get(cacheKey);
    if (joinedLoad) {
      try {
        applySnapshot(await joinedLoad);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load Revit data';
        setError(errorMessage);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    const requestPromise = (async (): Promise<RevitDataSnapshot> => {
      const elemsPromise = scopedParameterNames
        ? revitBridge.getElements({ parameterNames: scopedParameterNames })
        : Promise.resolve<RevitApiResponse<RevitElement[]>>({ success: true, data: [] });
      const familiesPromise = scopedParameterNames
        ? (refresh ? refreshFamilySnapshotResult : fetchFamilySnapshotResult)({
            parameterNames: scopedParameterNames,
          })
        : Promise.resolve<FamilySnapshotResult>({ available: true, families: [] });
      const settingsPromise = revitBridge.getSettings();

      const [elementsResponse, familiesResponse, settingsResponse] =
        await withTimeout(
          Promise.all([elemsPromise, familiesPromise, settingsPromise]),
          API_TIMEOUT_MS,
          'Revit API request timed out. Please ensure Revit is running and try again.'
        );

      const elemsData = elementsResponse.success && Array.isArray(elementsResponse.data)
        ? elementsResponse.data
        : [];
      const familiesData = familiesResponse.available && Array.isArray(familiesResponse.families)
        ? familiesResponse.families
        : [];
      const safeFamilies = familiesData.map(family => ({
        ...family,
        types: Array.isArray(family.types) ? family.types : [],
        parameters: Array.isArray(family.parameters) ? family.parameters : [],
      }));

      if (!elementsResponse.success) {
        console.warn('[RevitDataContext] Elements response not successful:', elementsResponse.error);
      }
      if (!familiesResponse.available) {
        console.warn('[RevitDataContext] Families response not successful:', getResponseError(familiesResponse));
      }

      return resolveE2eQuickFillMixedScopeSnapshot({
        parameters: [],
        sharedParameters: [],
        elements: elemsData,
        families: safeFamilies,
        settings: settingsResponse.success && settingsResponse.data ? settingsResponse.data : null,
      });
    })();

    inFlightLoadsRef.current.set(cacheKey, requestPromise);

    try {
      const snapshot = await requestPromise;
      responseCacheRef.current.set(cacheKey, cloneSnapshot(snapshot));
      applySnapshot(snapshot);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load Revit data';
      setError(errorMessage);
      console.error('[RevitDataContext] Error loading Revit data:', err);
    } finally {
      setIsLoading(false);
      inFlightLoadsRef.current.delete(cacheKey);
    }
  }, [applySnapshot]);

  const loadRevitApiData = useCallback(
    async (specificParameterNames?: string[]) =>
      loadRevitApiDataInternal(specificParameterNames),
    [loadRevitApiDataInternal]
  );

  const refreshData = useCallback(async (specificParameterNames?: string[]) => {
    await loadRevitApiDataInternal(specificParameterNames, true);
  }, [loadRevitApiDataInternal]);

  return (
    <RevitDataContext.Provider
      value={{
        parameters,
        sharedParameters,
        elements,
        families,
        settings,
        isLoaded,
        isLoading,
        error,
        loadRevitApiData,
        refreshData,
      }}
    >
      {children}
    </RevitDataContext.Provider>
  );
};

// Default context value for when provider is not available
const defaultContextValue: RevitDataContextType = {
  parameters: [],
  sharedParameters: [],
  elements: [],
  families: [],
  settings: null,
  isLoaded: false,
  isLoading: false,
  error: 'RevitDataProvider not found',
  loadRevitApiData: async (_specificParameterNames?: string[]) => {
    console.error('[useRevitData] Cannot load data - provider not available');
  },
  refreshData: async (_specificParameterNames?: string[]) => {
    console.error('[useRevitData] Cannot refresh data - provider not available');
  },
};

// Custom hook for consuming context
export const useRevitData = (): RevitDataContextType => {
  const context = useContext(RevitDataContext);
  if (!context) {
    // Instead of throwing, return default value and log warning
    // This prevents white screen crashes
    console.warn('[useRevitData] Context not available - returning default values');
    return defaultContextValue;
  }
  return context;
};

// Selector hooks for optimized re-renders
export const useRevitParameters = () => useRevitData().parameters;
export const useRevitSharedParameters = () => useRevitData().sharedParameters;
export const useRevitElements = () => useRevitData().elements;
export const useRevitFamilies = () => useRevitData().families;
export const useRevitSettings = () => useRevitData().settings;
